import React from 'react';
import { Plus, Package, Check, RotateCcw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/** Card prodotto riusata identica in "Reso da Documenti" e "Reso per
 * Prodotto" (richiesta utente 24/02/2026). Regola di eleggibilità
 * unificata: se il documento da cui si rende risale oltre la Finestra di
 * reso (12 mesi fissi, ``rma_return_window_months`` del backend), l'opacità
 * della card NON viene ridotta — al posto del bottone "Aggiungi al reso"
 * viene mostrata solo la dicitura "Non restituibile".
 *
 * ``returnWindowMonths`` va passato dal chiamante: lo stesso valore per i due
 * percorsi, così un articolo non è restituibile da uno e non dall'altro. */
export interface RMAProductCardProps {
  sku: string;
  title: string;
  brand?: string;
  quantity: number;
  alreadyRequested: number;
  unitPrice: number;
  inCart: number;
  orderDate: string;
  returnWindowMonths: number;
  extraMeta?: React.ReactNode;
  onAdd: (effectiveMaxQty: number) => void;
  testId?: string;
}

function isWithinReturnWindow(dateStr: string, months: number): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  return d >= cutoff;
}

export function RMAProductCard({
  sku, title, brand, quantity, alreadyRequested, unitPrice, inCart, orderDate, returnWindowMonths, extraMeta, onAdd, testId,
}: RMAProductCardProps) {
  const { canOpenRma } = useAuth();
  const eligible = isWithinReturnWindow(orderDate, returnWindowMonths);
  const effectiveMaxQty = Math.max(0, quantity - alreadyRequested);
  const remaining = effectiveMaxQty - inCart;
  const fullyAdded = effectiveMaxQty > 0 && remaining <= 0;

  return (
    <div className="rounded-xl border bg-white p-4 flex items-center gap-4" data-testid={testId}>
      <div className="w-10 h-10 rounded-lg bg-secondary/60 flex items-center justify-center flex-shrink-0">
        <Package className="w-5 h-5 text-muted-foreground" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="font-mono text-[10px] text-muted-foreground">Cod. {sku}</span>
          {brand && <span className="text-[10px] font-bold border px-1.5 py-0.5 rounded bg-secondary/60">{brand}</span>}
        </div>
        <div className="text-sm font-medium line-clamp-2" title={title}>{title}</div>
        {extraMeta}
        <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
          <span>Acquistati: <span className="font-medium text-foreground">{quantity}</span></span>
          {alreadyRequested > 0 && (
            <span>Già richiesti in reso: <span className="font-medium text-foreground">{alreadyRequested}</span></span>
          )}
          <span>Disponibili per il reso: <span className="font-medium text-foreground">{effectiveMaxQty}</span></span>
          <span>€ {unitPrice.toFixed(2).replace('.', ',')} cad.</span>
          {inCart > 0 && (
            <span className="text-emerald-600 font-medium flex items-center gap-1">
              <Check className="w-3 h-3" /> {inCart} nel carrello resi
            </span>
          )}
        </div>
      </div>

      <div className="flex-shrink-0">
        {!eligible ? (
          <span className="text-xs text-red-600 font-medium px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 inline-flex items-center" data-testid={`${testId}-not-returnable`}>
            Non restituibile
          </span>
        ) : effectiveMaxQty <= 0 ? (
          <span className="text-xs text-muted-foreground px-3 py-1.5 rounded-lg bg-secondary/60 border border-border inline-flex items-center gap-1" data-testid={`${testId}-fully-requested`}>
            <RotateCcw className="w-3 h-3" /> Reso già richiesto
          </span>
        ) : fullyAdded ? (
          <span className="text-xs text-emerald-700 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200 inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> Tutti aggiunti
          </span>
        ) : !canOpenRma ? (
          <span className="text-xs text-muted-foreground px-3 py-1.5 rounded-lg bg-secondary/60 border border-border inline-flex items-center" data-testid={`${testId}-not-allowed`}>
            Reso non abilitato
          </span>
        ) : (
          <button
            onClick={() => onAdd(effectiveMaxQty)}
            className="h-9 px-4 rounded-lg bg-[hsl(214,58%,41%)] text-white text-xs font-medium flex items-center gap-1.5 hover:bg-[hsl(214,58%,46%)] transition-colors"
            data-testid={`${testId}-add`}
          >
            <Plus className="w-3.5 h-3.5" /> Aggiungi al reso
          </button>
        )}
      </div>
    </div>
  );
}
